/**
 * Main Application Bootstrap & State Coordinator
 * Bridges the 3D WebGL Scene with the Telemetry HUD, Layer Controls,
 * High-Resolution Reconnaissance Inspector, and Astrological Ephemeris.
 */

import './style.css';
import { MoonScene } from './engine/MoonScene.js';
import { TelemetryHUD } from './components/TelemetryHUD.js';
import { LayerControls } from './components/LayerControls.js';
import { ReconModal } from './components/ReconModal.js';
import { AstrologyPanel } from './components/AstrologyPanel.js';
import { SelenologyPanel } from './components/SelenologyPanel.js';
import { CreditsModal } from './components/CreditsModal.js';

class MoonApplication {
  constructor() {
    this.container = document.getElementById('canvas-container');
    this.uiContainer = document.getElementById('ui-container');

    this.initEngine();
    this.initComponents();
    this.initTopNav();
    window.moonApp = this;
  }

  initEngine() {
    this.scene = new MoonScene(this.container);

    // Surface Reconnaissance trigger on marker click
    this.scene.onFeatureClick = (feature) => {
      this.scene.orbitManager.flyTo(feature.lat, feature.lon, 1.45, () => {
        this.reconModal.show(feature);
      });
    };

    // Telemetry callback
    this.scene.onTelemetryUpdate = (telemetry) => {
      if (this.telemetryHUD) {
        this.telemetryHUD.update(telemetry);
      }
    };
  }

  initComponents() {
    // 1. SRE Telemetry HUD
    this.telemetryHUD = new TelemetryHUD(this.uiContainer);

    // 2. Reconnaissance Modal
    this.reconModal = new ReconModal(this.uiContainer, (feature) => {
      this.scene.orbitManager.flyTo(feature.lat, feature.lon, 1.35);
    });

    // 3. Astrology & Ephemeris Panel
    this.astrologyPanel = new AstrologyPanel(this.uiContainer, (phaseAngle) => {
      // Rotate directional sun light according to phase angle
      const lightDistance = 25;
      const lightX = lightDistance * Math.cos(phaseAngle);
      const lightZ = lightDistance * Math.sin(phaseAngle);
      this.scene.moonGlobe.setSunPosition({ x: lightX, y: 3, z: lightZ });
    });

    // 4. Selenology & Geology Panel
    this.selenologyPanel = new SelenologyPanel(this.uiContainer);

    // 5. Copyright & Source Attribution Modal
    this.creditsModal = new CreditsModal(this.uiContainer);

    // 6. Layer Controls Bar
    this.layerControls = new LayerControls(this.uiContainer, {
      onLayerChange: (mode) => {
        this.scene.moonGlobe.setLayerMode(mode);
      },
      onToggleGrid: (visible) => {
        this.scene.grid.setVisible(visible);
      },
      onToggleMarkers: (visible) => {
        this.scene.markers.setVisible(visible);
      },
      onToggleLight: (isStudio) => {
        this.scene.moonGlobe.setSunPosition(this.scene.moonGlobe.sunDirection, isStudio);
      },
      onToggleRotate: (active) => {
        this.scene.autoRotate = active;
      },
      onNavNearside: () => {
        this.scene.orbitManager.resetToNearside();
      },
      onNavFarside: () => {
        this.scene.orbitManager.flipToFarside();
      },
      onNavSouthPole: () => {
        this.scene.orbitManager.resetToSouthPole();
      },
      onNavNorthPole: () => {
        this.scene.orbitManager.resetToNorthPole();
      },
      onSelectLandmark: (feature) => {
        this.scene.orbitManager.flyTo(feature.lat, feature.lon, 1.45, () => {
          this.reconModal.show(feature);
        });
      }
    });
  }

  initTopNav() {
    const btnAstro = document.getElementById('btn-open-astrology');
    const btnSeleno = document.getElementById('btn-open-selenology');
    const btnCredits = document.getElementById('btn-open-credits');
    const btnFooterCredits = document.getElementById('btn-footer-credits');

    btnAstro.addEventListener('click', () => {
      this.selenologyPanel.hide();
      this.creditsModal.hide();
      this.astrologyPanel.toggle();
    });

    btnSeleno.addEventListener('click', () => {
      this.astrologyPanel.hide();
      this.creditsModal.hide();
      this.selenologyPanel.toggle();
    });

    if (btnCredits) {
      btnCredits.addEventListener('click', () => {
        this.astrologyPanel.hide();
        this.selenologyPanel.hide();
        this.creditsModal.toggle();
      });
    }

    if (btnFooterCredits) {
      btnFooterCredits.addEventListener('click', () => {
        this.astrologyPanel.hide();
        this.selenologyPanel.hide();
        this.creditsModal.show();
      });
      btnFooterCredits.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          this.creditsModal.show();
        }
      });
    }
  }
}

// Bootstrap once DOM is ready
window.addEventListener('DOMContentLoaded', () => {
  new MoonApplication();
});
